import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMetadataElementsToCanvas as addMetadataElementsToCanvasAction,
  selectIsFunnelMapEmpty,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import {
  setIsLoadingCursor,
  setTransientFunnel,
} from '../../../../reducers/canvasPageStateReducer';
import { FunnelType } from '../../../../types/enums/FunnelType';
import { getFullMetadataElementId } from './metadataElementUtils';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { telemetry } from '../../../../telemetry';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { EnrichedMetadataElementDto } from '@server/metadata-elements';

export type FunnelMapPosition = {
  column: number;
  row: number;
};

export type FunnelMapMetadataElement = EnrichedMetadataElementDto & {
  position?: FunnelMapPosition;
  parentType: string;
};

export type CanvasConnection = {
  source: string;
  target: string;
  targetType: FunnelType;
};

export type MetadataElementWithoutConnections = Omit<
  FunnelMapMetadataElement,
  'dependencyConnections' | 'usedByConnections'
>;

const DEFAULT_POSITION: FunnelMapPosition = { column: 0, row: 0 };

export const addDefaultPositions = (metadataElements: FunnelMapMetadataElement[]) => {
  return metadataElements.map((element) => ({
    name: element.name,
    type: element.type,
    crmOrgId: element.crmOrgId,
    metadataId: element.metadataId,
    position: element.position || DEFAULT_POSITION,
    color: element.color,
    icon: element.icon,
  }));
};

/**
 * Assigns funnel map connections from metadata elements
 */
export const assignFunnelMapConnectionsFromMetadataElements = (
  metadataElements: FunnelMapMetadataElement[],
  canvasFunnelMap: FunnelMap,
): CanvasConnection[] => {
  const allowedConnections = Object.values(canvasFunnelMap.metadataElements).flatMap(
    (metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    }),
  );
  allowedConnections.push(
    ...metadataElements.map((metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    })),
  );

  const connections = metadataElements.flatMap((metadataElement) => {
    const usedByConnections = createUsedByConnections(metadataElement);
    const dependenciesConnections = createDependenciesConnections(metadataElement);
    return [...usedByConnections, ...dependenciesConnections];
  });
  return connections.filter(
    (connection) =>
      allowedConnections.map(getFullMetadataElementId).includes(connection.source) &&
      allowedConnections.map(getFullMetadataElementId).includes(connection.target),
  );
};

const createUsedByConnections = (metadataElement: FunnelMapMetadataElement): CanvasConnection[] => {
  return (
    metadataElement.usedByConnections?.map((connection) => ({
      source: getFullMetadataElementId(connection),
      target: getFullMetadataElementId(metadataElement),
      targetType: FunnelType.METADATA_ELEMENT,
    })) ?? []
  );
};

const createDependenciesConnections = (
  metadataElement: FunnelMapMetadataElement,
): CanvasConnection[] => {
  return (
    metadataElement.dependencyConnections?.map((connection) => ({
      source: getFullMetadataElementId(metadataElement),
      target: getFullMetadataElementId(connection),
      targetType: FunnelType.METADATA_ELEMENT,
    })) ?? []
  );
};

export const useAddMetadataElementToCanvas = (skipLocating?: boolean) => {
  const dispatch = useDispatch();
  const isFunnelMapEmpty = useSelector(selectIsFunnelMapEmpty);

  const addMetadataElementsToCanvas = useCallback(
    (metadataElements: FunnelMapMetadataElement[], canvasFunnelMap: FunnelMap) => {
      const metadataElementsWithPosition = addDefaultPositions(metadataElements);

      const connections = assignFunnelMapConnectionsFromMetadataElements(
        metadataElements,
        canvasFunnelMap,
      );

      const action =
        isFunnelMapEmpty || skipLocating
          ? addMetadataElementsToCanvasAction({
              metadataElements: metadataElementsWithPosition,
              connections,
            })
          : setTransientFunnel({
              type: FunnelType.METADATA_ELEMENT,
              importType: 'import',
              data: {
                metadataElements: metadataElementsWithPosition,
                connections,
              },
              isPlacingFunnel: true,
            });

      dispatch(action);
    },
    [dispatch, isFunnelMapEmpty, skipLocating],
  );

  const { post_enrichMetadataElements } = useFunnelMapApiFacade();
  const { addNotification } = useSweepNotifications();

  const getMetadataElementsWithConnections = useCallback(
    async ({
      metadataElements,
      parentElement,
      commonChild,
    }: {
      metadataElements: MetadataElementWithoutConnections[];
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      };
      commonChild?: {
        metadataId: string;
        crmOrgId: string;
      };
    }) => {
      if (metadataElements.length === 0) {
        return [];
      }

      const crmOrgId = metadataElements[0].crmOrgId;
      if (!metadataElements.every((el) => el.crmOrgId === crmOrgId)) {
        throw new Error('All metadata elements must belong to the same crmOrgId');
      }

      const metadataElementIds = metadataElements.map(getFullMetadataElementId);

      const parentElementId = parentElement ? getFullMetadataElementId(parentElement) : undefined;
      const commonChildId = commonChild ? getFullMetadataElementId(commonChild) : undefined;

      const enrichedElements = await post_enrichMetadataElements(crmOrgId, {
        metadataElementIds,
        parentElementId,
        commonChildId,
      });

      const originalElementsMap = new Map(
        metadataElements.map((el) => [getFullMetadataElementId(el), el]),
      );

      const metadataElementsWithConnections: FunnelMapMetadataElement[] = enrichedElements.map(
        (enriched) => {
          const fullId = getFullMetadataElementId(enriched);
          const original = originalElementsMap.get(fullId);

          const { dependencyConnections, usedByConnections, ...rest } = enriched;
          return {
            ...rest,
            dependencyConnections,
            usedByConnections,
            position: original?.position,
            parentType: original?.parentType || enriched.type,
          };
        },
      );

      return metadataElementsWithConnections;
    },
    [post_enrichMetadataElements],
  );

  const addMetadataElementsToCanvasAndEnrichConnections = useCallback(
    async ({
      metadataElements,
      canvasFunnelMap,
      parentElement,
      commonChild,
    }: {
      metadataElements: MetadataElementWithoutConnections[];
      canvasFunnelMap: FunnelMap;
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      };
      commonChild?: {
        metadataId: string;
        crmOrgId: string;
      };
    }) => {
      dispatch(setIsLoadingCursor(true));
      try {
        const metadataElementsWithConnections = await getMetadataElementsWithConnections({
          metadataElements,
          parentElement,
          commonChild,
        });

        addMetadataElementsToCanvas(metadataElementsWithConnections, canvasFunnelMap);
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: 'Failed to add metadata elements to canvas',
          variant: SweepNotificationVariant.Error,
        });
      } finally {
        dispatch(setIsLoadingCursor(false));
      }
    },
    [addMetadataElementsToCanvas, addNotification, dispatch, getMetadataElementsWithConnections],
  );

  return {
    addMetadataElementsToCanvasAndEnrichConnections,
    getMetadataElementsWithConnections,
  };
};
